/**
 * Slide Canvas
 * A fixed 1920 × 1080 slide, scaled to fit the screen whole
 */

import React, { useLayoutEffect, useRef, useState } from 'react';
import styled from 'styled-components';

/** The design size — and the size the PDF/PPTX export captures. */
export const CANVAS_W = 1920;
export const CANVAS_H = 1080;

/**
 * For a slide composed as one picture — a homepage section laid out to be
 * taken in at a glance — rather than reflowed for each screen. Its children
 * are placed in canvas pixels and the canvas is scaled to fit the slide
 * whole, centred, so any screen shows the composition the export captures.
 */
export const SlideCanvas: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const frameRef = useRef<HTMLDivElement>(null);
  const [fit, setFit] = useState(0);

  useLayoutEffect(() => {
    const frame = frameRef.current;
    if (!frame) return undefined;
    const measure = () => setFit(Math.min(frame.clientWidth / CANVAS_W, frame.clientHeight / CANVAS_H));
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(frame);
    return () => observer.disconnect();
  }, []);

  return (
    <Frame ref={frameRef}>
      <Canvas style={{ transform: `translate(-50%, -50%) scale(${fit})` }}>{children}</Canvas>
    </Frame>
  );
};

const Frame = styled.div`
  position: absolute;
  inset: 0;
  overflow: hidden;
`;

const Canvas = styled.div`
  position: absolute;
  left: 50%;
  top: 50%;
  width: ${CANVAS_W}px;
  height: ${CANVAS_H}px;
  transform-origin: 50% 50%;
`;
